import { useRef, useState } from 'react';
import { useCampaignBuilderV2Api } from '../api';
import { CampaignPolicy } from '../policies';
import {
  generateCampaignCopyFallbackName,
  generateUniqueCampaignName,
} from '../helpers/uniqueName';
import { Campaign, CampaignStatus, SaveMode } from '../types';
import { CampaignForm } from './useCampaignFormState';
import { SubmitOptions } from './useCampaignForm';
import { useActivationGate } from './useActivationGate';
import { useBlocklistGuard } from './useBlocklistGuard';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { Keyword } from 'src/modules/keywords/types';
import { isKeywordClaimedElsewhere } from '../helpers/keywordHelpers';

interface UseCampaignSaveParams {
  policy: CampaignPolicy;
  form: CampaignForm;
  submit: (status: CampaignStatus, mode: SaveMode, options?: SubmitOptions) => Promise<unknown>;
  campaign?: Campaign | null;
  isDuplicate: boolean;
  isExistingCampaign: boolean;
  companyId: string;
  keywords?: Keyword[];
}

/**
 * The three checks that run before a campaign is persisted, kept out of the
 * orchestrator so it stays a rendering component:
 *
 *  1. Activation gate — the company must be onboarded and subscribed.
 *  2. Save-as-new-draft — editing a live campaign and saving it as a draft
 *     creates a copy under a unique name.
 *  3. Blocklist — activation scans the campaign's copy for restricted words.
 *
 * Drafts skip 1 and 3.
 */
export function useCampaignSave({
  policy,
  form,
  submit,
  campaign,
  isDuplicate,
  isExistingCampaign,
  companyId,
  keywords = [],
}: UseCampaignSaveParams) {
  const campaignApi = useCampaignBuilderV2Api();
  const blocklist = useBlocklistGuard();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const isSavingLiveCampaignAsDraft = (status: CampaignStatus) =>
    status === 'draft' && !!campaign?.status && campaign.status !== 'draft' && !isDuplicate;

  const saveDraft = async (options?: SubmitOptions) => {
    if (isSavingLiveCampaignAsDraft('draft')) {
      const originalName = form.getValues().name;
      let uniqueName: string;
      try {
        uniqueName = await generateUniqueCampaignName(
          originalName,
          companyId,
          (cid: string) => campaignApi.listCampaigns(cid).then((r) => r.data),
        );
      } catch (error) {
        // Preserve the old builder's behavior: a temporary list failure must
        // not prevent somebody from parking a live campaign as a draft.
        console.error('Failed to generate unique campaign name:', error);
        uniqueName = generateCampaignCopyFallbackName(originalName);
      }
      return submit('draft', 'create', { ...options, valuesOverride: { name: uniqueName } });
    }
    return submit('draft', isExistingCampaign ? 'update' : 'create', options);
  };

  const { blocksActivation, showOnboardingGate, activationGate } = useActivationGate({
    saveAsDraft: saveDraft,
  });

  // The backend can also reject activation for the same reason the client-side gate checks, so
  // surface the dialog either way.
  const activationOptions: SubmitOptions = {
    onFailedDependency: showOnboardingGate,
    validateAsActive: true,
    afterPersist: async savedCampaign => {
      // A duplicate still carries its source campaign through the builder, but
      // after persistence it is a brand-new campaign. Activating the source here
      // leaves the copy inactive and can re-trigger side effects on the original.
      const id = isExistingCampaign ? campaign?.id : savedCampaign?.id;
      if (id) await campaignApi.activateCampaign([id], companyId);
    },
  };

  // Covers the blocklist request too, which runs before the form reports saving. Without it a
  // save confirmed from the keyword warning left the buttons enabled for that request.
  const [isChecking, setIsChecking] = useState(false);
  const checkingRef = useRef(false);

  const persist = async (status: CampaignStatus, mode: SaveMode, activate: boolean) => {
    if (status === 'draft' && !activate) return saveDraft();
    if (checkingRef.current) return;
    checkingRef.current = true;
    setIsChecking(true);
    try {
      const texts = policy.blocklistTexts(form.getValues());
      const options = activate ? activationOptions : undefined;
      const ok = await blocklist.check(texts, () => submit(status, mode, options));
      if (!ok) return;

      return await submit(status, mode, options);
    } finally {
      checkingRef.current = false;
      setIsChecking(false);
    }
  };

  const save = async (status: CampaignStatus, mode: SaveMode, activate = status === 'active') => {
    if (activate && blocksActivation()) return;

    const { keywordIds } = form.getValues();
    // Only an actual edit owns the source campaign's keyword associations. A
    // duplicate is new, so its source must count as another active campaign.
    const currentCampaignId = isExistingCampaign ? campaign?.id : undefined;
    const hasKeywordConflict = (keywordIds ?? []).some(keywordId =>
      isKeywordClaimedElsewhere(keywords, keywordId, currentCampaignId));
    if (hasKeywordConflict) {
      openDialog({
        title: 'WARNING!',
        content:
          'Some of the keywords are already part of another active campaign, which may cause a bad experience for the recipient. Are you sure you want to continue?',
        onConfirm: () => { void persist(status, mode, activate); },
        confirmButtonText: 'Continue',
      });
      return;
    }

    return persist(status, mode, activate);
  };

  return { save, isChecking, blocklist, activationGate, KeywordConfirmationDialog: ConfirmationDialog };
}
